import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Activity, AlertCircle, CheckCircle2, XCircle, TrendingUp, Database } from "lucide-react";
import { LineChart, Line, BarChart, Bar, PieChart, Pie, Cell, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from "recharts";
import { formatDistanceToNow } from "date-fns";

export default function IntegrationHealth() {
  const { data: connections } = useQuery({
    queryKey: ["integration-connections"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("integration_connections")
        .select("*");
      if (error) throw error;
      return data;
    },
  });

  const { data: syncLogs } = useQuery({
    queryKey: ["integration-sync-logs-health"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("integration_sync_logs")
        .select("*")
        .gte("started_at", new Date(Date.now() - 24 * 60 * 60 * 1000).toISOString())
        .order("started_at", { ascending: false });
      if (error) throw error;
      return data;
    },
  });

  const { data: dataQuality } = useQuery({
    queryKey: ["data-quality-metrics"],
    queryFn: async () => {
      const { data: orphanedAthletes } = await supabase
        .from("athletes")
        .select("id")
        .is("external_student_id", null);

      const { data: unmappedStaff } = await supabase
        .from("spry_staff_sync")
        .select("id")
        .eq("sync_status", "pending");

      return {
        orphaned_athletes: orphanedAthletes?.length || 0,
        unmapped_staff: unmappedStaff?.length || 0,
      };
    },
  });

  const activeConnection = connections?.find(c => c.status === "active");
  const last24hLogs = syncLogs || [];
  const successRate = last24hLogs.length > 0
    ? (last24hLogs.filter(l => l.status === "completed").length / last24hLogs.length) * 100
    : 0;

  const syncStatusData = [
    { name: "Completed", value: last24hLogs.filter(l => l.status === "completed").length, color: "#10B981" },
    { name: "Failed", value: last24hLogs.filter(l => l.status === "failed").length, color: "#EF4444" },
    { name: "Partial", value: last24hLogs.filter(l => l.status === "partial").length, color: "#F59E0B" },
  ].filter(d => d.value > 0);

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-3xl font-bold tracking-tight">Integration Health</h1>
        <p className="text-muted-foreground mt-2">
          Monitor integration status, sync performance, and data quality metrics
        </p>
      </div>

      {/* Alerts */}
      {activeConnection?.status === "error" && (
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>
            Integration connection error: {activeConnection.last_error}
          </AlertDescription>
        </Alert>
      )}

      {successRate < 95 && last24hLogs.length > 0 && (
        <Alert>
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>
            Sync success rate is below 95% ({successRate.toFixed(1)}%)
          </AlertDescription>
        </Alert>
      )}

      {/* Connection Status */}
      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Connection Status</CardTitle>
            {activeConnection?.status === "active" ? (
              <CheckCircle2 className="h-4 w-4 text-green-500" />
            ) : (
              <XCircle className="h-4 w-4 text-destructive" />
            )}
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {activeConnection?.status || "Not Connected"}
            </div>
            {activeConnection?.last_successful_sync && (
              <p className="text-xs text-muted-foreground">
                Last sync {formatDistanceToNow(new Date(activeConnection.last_successful_sync), { addSuffix: true })}
              </p>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">24h Success Rate</CardTitle>
            <TrendingUp className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{successRate.toFixed(1)}%</div>
            <p className="text-xs text-muted-foreground">
              {last24hLogs.length} syncs in last 24 hours
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Records Synced</CardTitle>
            <Database className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {last24hLogs.reduce((sum, log) => sum + log.records_processed, 0)}
            </div>
            <p className="text-xs text-muted-foreground">
              Total processed in 24h
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Data Quality Issues</CardTitle>
            <AlertCircle className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {(dataQuality?.orphaned_athletes || 0) + (dataQuality?.unmapped_staff || 0)}
            </div>
            <p className="text-xs text-muted-foreground">
              Orphaned athletes + unmapped staff
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Charts */}
      <div className="grid gap-4 md:grid-cols-2">
        <Card>
          <CardHeader>
            <CardTitle>Sync Status Distribution</CardTitle>
            <CardDescription>Last 24 hours</CardDescription>
          </CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={300}>
              <PieChart>
                <Pie
                  data={syncStatusData}
                  cx="50%"
                  cy="50%"
                  labelLine={false}
                  label={({ name, value }) => `${name}: ${value}`}
                  outerRadius={80}
                  fill="#8884d8"
                  dataKey="value"
                >
                  {syncStatusData.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={entry.color} />
                  ))}
                </Pie>
                <Tooltip />
              </PieChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Records Processed</CardTitle>
            <CardDescription>By sync job</CardDescription>
          </CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={last24hLogs.slice(0, 10).reverse()}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="entity_type" />
                <YAxis />
                <Tooltip />
                <Legend />
                <Bar dataKey="records_created" fill="#10B981" name="Created" />
                <Bar dataKey="records_updated" fill="#3B82F6" name="Updated" />
                <Bar dataKey="records_failed" fill="#EF4444" name="Failed" />
              </BarChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>
      </div>

      {/* Data Quality */}
      <Card>
        <CardHeader>
          <CardTitle>Data Quality Metrics</CardTitle>
          <CardDescription>Issues requiring attention</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="font-medium">Orphaned Athletes</p>
                <p className="text-sm text-muted-foreground">
                  Athletes without external student ID
                </p>
              </div>
              <Badge variant={dataQuality?.orphaned_athletes === 0 ? "default" : "destructive"}>
                {dataQuality?.orphaned_athletes || 0}
              </Badge>
            </div>
            <div className="flex items-center justify-between">
              <div>
                <p className="font-medium">Unmapped Staff</p>
                <p className="text-sm text-muted-foreground">
                  Staff from Spry not mapped to user accounts
                </p>
              </div>
              <Badge variant={dataQuality?.unmapped_staff === 0 ? "default" : "secondary"}>
                {dataQuality?.unmapped_staff || 0}
              </Badge>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
